import { Box, Paper, Typography, Chip, Divider } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { EMManagementIdHeader } from '../../EMManagementIdHeader';
import { PATHS } from 'routes/constants';
import { TEmailEventItem, TEmailListItem } from 'redux/reducers/emails/types';
import { useDispatch, useSelector } from 'react-redux';
import {
	getEmailsListData,
	getEmailsListLoading,
	getEmailsTypesData,
} from 'redux/reducers/emails/selectors';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { eventsTableStructure, TEmailEventRow } from './eventsTableStructure';
import { COLORS } from '../../../../../services/constants/colors';
import { EasTableValue } from '../../../../../ui/EasTable/EasTableRow/EasTableValue';
import { ETableStructureType } from '../../../../../ui/EasTable/types';
import {
	getEmailsListRequest,
	getEmailsTypesRequest,
} from '../../../../../redux/reducers/emails/reducer';
import { tableStructure } from '../tableStructure';
import { getSavedUrl2, getSavedUrl3 } from '../../../../../redux/reducers/auth/selectors';

const formatDateTime = (iso: string | null) =>
	iso ? DateTime.fromISO(iso).toFormat('yyyy-MM-dd, HH:mm') : '—';

export const CommunicationHistoryDetail = () => {
	const { emId, emailId } = useParams<{ emId?: string; emailId?: string }>();
	const navigate = useNavigate();
	const emailsList = useSelector(getEmailsListData);
	const emailsListLoading = useSelector(getEmailsListLoading);
	const emailTypes = useSelector(getEmailsTypesData);
	const dispatch = useDispatch();
	const savedUrl2 = useSelector(getSavedUrl2);
	const savedUrl3 = useSelector(getSavedUrl3);
	useEffect(() => {
		if (emailId) {
			dispatch(
				getEmailsListRequest({
					pageNumber: 1,
					pageSize: 1,
					searchString: '',
					filters: [{ fieldName: 'id', value: String(emailId ?? ''), operator: '=' }],
					orderBy: [],
				}),
			);
		}
		dispatch(getEmailsTypesRequest());
	}, [emId, dispatch, emailId]);

	const bounceReason = useMemo(() => {
		const bounceEvent = emailsList?.data?.[0]?.events?.find(
			(e: TEmailEventItem) => e.value === 'bounce',
		)?.comments;
		return bounceEvent ?? null;
	}, [emailsList?.data?.[0]?.events]);

	const typeDescription =
		emailsList?.data?.[0]?.typeHandle &&
		emailTypes?.find((t) => t.handle === emailsList?.data?.[0]?.typeHandle)?.description;
	const events = emailsList?.data?.[0]?.events;
	const eventsPageResponse = useMemo(() => {
		if (!events?.length) return null;
		const data: TEmailEventRow[] = events.map((ev: TEmailEventItem, idx: number) => ({
			...ev,
			id: idx,
		}));
		return { data, pageNumber: 1, pageSize: data.length, totalCount: data.length };
	}, [events]);

	if (!emailsList?.data?.[0] || String(emailsList?.data?.[0]?.id) !== emailId) {
		return (
			<Box sx={{ margin: '0px 10px 0 10px' }}>
				<Box sx={{ mb: 2 }}>
					<EMManagementIdHeader savedUrl={savedUrl3} />
				</Box>
				<Paper sx={{ p: 2 }}>
					<Typography color="text.secondary">
						Email not found. Go back to{' '}
						<Typography
							component="span"
							color="primary"
							sx={{ cursor: 'pointer', textDecoration: 'underline' }}
							onClick={() =>
								navigate(
									PATHS.EM_MANAGEMENT_COMMUNICATION_HISTORY_PAGE.replace(
										':emId',
										String(emId ?? ''),
									),
								)
							}
						>
							Communication History
						</Typography>
						.
					</Typography>
				</Paper>
			</Box>
		);
	}

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<Box sx={{ mb: 2 }}>
				<EMManagementIdHeader savedUrl={savedUrl3} />
			</Box>

			<Paper sx={{ borderRadius: '12px', mb: 2 }}>
				<Box
					sx={{
						backgroundColor: COLORS.BACKGROUND_SECONDARY,
						p: 0.5,
						borderRadius: '12px 12px 0 0',
					}}
				>
					<Typography
						variant="h6"
						color="primary"
						sx={{ ml: 1, fontWeight: 'bold', fontSize: '1rem' }}
					>
						Email details
					</Typography>
				</Box>
				<Box sx={{ p: 2 }}>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
						<Box>
							<Typography variant="subtitle2" color="primary">
								Status
							</Typography>
							<Box sx={{ mt: 0.25 }}>
								{/* <Chip
									label={emailsList?.data?.[0]?.status}
									size="small"
									color={
										emailsList?.data?.[0]?.status === 'open'
											? 'info'
											: emailsList?.data?.[0]?.status === 'bounced'
												? 'error'
												: 'success'
									}
								/> */}
								<EasTableValue
									type={ETableStructureType.STATUS}
									value={emailsList?.data?.[0]?.status}
									// item={emailsList?.data?.[0]}
									tableStructureItem={tableStructure.find((t) => t.selector === 'status')}
									bold
									size="m"
								/>
							</Box>
						</Box>
						<Box>
							<Typography variant="subtitle2" color="primary">
								Type
							</Typography>
							<Typography variant="body2">
								{typeDescription ?? emailsList?.data?.[0]?.typeHandle ?? '—'}
							</Typography>
						</Box>
						<Box sx={{ minWidth: 200 }}>
							<Typography variant="subtitle2" color="primary">
								Subject
							</Typography>
							<Typography variant="body2">{emailsList?.data?.[0]?.subject || '—'}</Typography>
						</Box>
					</Box>
					{bounceReason && (
						<>
							<Typography variant="subtitle2" color="primary" sx={{ mb: 0.5, mt: 1 }}>
								Bounce reason
							</Typography>
							<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
								{bounceReason}
							</Typography>
						</>
					)}
					<Divider sx={{ my: 2 }} />
					<Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
						Recipients
					</Typography>

					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
						{emailsList?.data?.[0]?.recipients?.length
							? emailsList?.data?.[0]?.recipients.map((r: string) => (
									<EasTableValue
										key={r}
										type={ETableStructureType.STATUS}
										value={r}
										bold
										size="m"
									/>
									// <Chip key={r} label={r} size="small" variant="outlined" sx={{ mb: 0.5 }} />
								))
							: '—'}
					</Box>
					{emailsList?.data?.[0]?.ccRecipients?.length > 0 && (
						<>
							<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
								CC
							</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
								{emailsList?.data?.[0]?.ccRecipients.map((r: string) => (
									<Chip key={r} label={r} size="small" variant="outlined" />
								))}
							</Box>
						</>
					)}

					<Divider sx={{ my: 2 }} />
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'auto 1fr',
							gap: 1,
							alignItems: 'baseline',
						}}
					>
						<Typography variant="subtitle2" color="primary">
							Created at
						</Typography>
						<Typography variant="body2">
							{formatDateTime(emailsList?.data?.[0]?.createdAt)}
						</Typography>
						<Typography variant="subtitle2" color="primary">
							Sent at
						</Typography>
						<Typography variant="body2">{formatDateTime(emailsList?.data?.[0]?.sentAt)}</Typography>
						<Typography variant="subtitle2" color="primary">
							Delivered at
						</Typography>
						<Typography variant="body2">
							{formatDateTime(emailsList?.data?.[0]?.deliveredAt)}
						</Typography>
						<Typography variant="subtitle2" color="primary">
							Last opened at
						</Typography>
						<Typography variant="body2">
							{formatDateTime(emailsList?.data?.[0]?.lastOpenedAt)}
						</Typography>
					</Box>
				</Box>
			</Paper>

			{/* <Paper sx={{ p: 2, mb: 2 }}>
				<Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
					Recipients
				</Typography>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
					{email.recipients?.length
						? email.recipients.map((r) => (
								<Chip key={r} label={r} size="small" variant="outlined" sx={{ mb: 0.5 }} />
							))
						: '—'}
				</Box>
				{email.ccRecipients?.length > 0 && (
					<>
						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
							CC
						</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
							{email.ccRecipients.map((r) => (
								<Chip key={r} label={r} size="small" variant="outlined" />
							))}
						</Box>
					</>
				)}
			</Paper> */}

			{emailsList?.data?.[0]?.mailClient?.length > 0 && (
				<Paper sx={{ p: 2, mb: 2 }}>
					<Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
						Mail client
					</Typography>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
						{emailsList?.data?.[0]?.mailClient.map((c: string) => (
							<Chip key={c} label={c} size="small" />
						))}
					</Box>
				</Paper>
			)}

			{emailsList?.data?.[0]?.events?.length ? (
				<EasTableWithoutFilter<TEmailEventRow>
					tableStructure={eventsTableStructure}
					items={eventsPageResponse}
					itemsIsLoading={emailsListLoading}
					handleAction={() => {}}
				>
					<Typography
						variant="body1"
						color="primary"
						fontWeight="bold"
						sx={{
							ml: 1,
						}}
					>
						Events
					</Typography>
				</EasTableWithoutFilter>
			) : (
				<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
					No events
				</Typography>
			)}
		</Box>
	);
};
